import { Sentry } from './sentry';

export type PreloadErrorPayload = Error & { sourceURL?: string };

// Best-effort extraction of the failed chunk URL from a preload error. Safari
// puts it on `sourceURL`; other browsers embed it in the message/stack (e.g.
// "Failed to fetch dynamically imported module: <url>").
export const getChunkUrl = (payload?: PreloadErrorPayload): string | null => {
  if (!payload) {
    return null;
  }
  if (payload.sourceURL) {
    return payload.sourceURL;
  }
  const text = `${payload.message ?? ''}\n${payload.stack ?? ''}`;
  return text.match(/https?:\/\/\S+\.m?js/)?.[0] ?? null;
};

// LogRocket is best-effort session recording loaded via a dynamic import (see
// src/hooks/useAnalytics.ts), and its caller already tolerates a failed load.
// Ad/privacy blockers and school/corporate network filters routinely block it,
// so its chunk fails to fetch for a meaningful slice of users — but that failure
// is harmless. We give it a stable chunk name via vite `manualChunks`
// ('logrocket'); older deploys named the chunk after its dist entry
// ('build.umd'), so match both. A preload failure for one of these chunks must
// not trigger a full-page reload or Sentry noise (Sentry WEB-CLIENT-1H9).
export const isBestEffortChunk = (url: string | null): boolean =>
  url !== null && /\/(logrocket|build\.umd)\.[^/]*\.js$/.test(url);

// Reload when chunks from a previous deployment are still in the cache, but only
// report to Sentry if a reload didn't resolve it — the common case is a
// transient client-side network blip and is just noise.
const CHUNK_RELOAD_KEY = '_ah_chunk_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 30_000;
const CHUNK_CHECK_TIMEOUT_MS = 3_000;

// A SyntaxError means the chunk downloaded but wouldn't parse, which has two
// very different causes. Re-fetch it and read the content type to tell them
// apart:
//  - 'js':   valid JS the browser is too old to parse (unsupported/in-app
//            browsers, e.g. the Facebook browser on old iOS) — reloading
//            refetches the same file, so it's pure noise (Sentry WEB-CLIENT-1H9,
//            WEB-CLIENT-24K).
//  - 'html': the backend served its index.html fallback because the chunk is
//            missing (typically a stale deploy) — reloading recovers it, and a
//            failure that survives a reload is a real infra problem worth seeing.
type ChunkClassification = {
  kind: 'js' | 'html' | 'unknown';
  url: string | null;
};

const classifyFailedChunk = async (
  payload?: PreloadErrorPayload
): Promise<ChunkClassification> => {
  const url = getChunkUrl(payload);
  if (!url) {
    return { kind: 'unknown', url };
  }
  try {
    const canTimeout =
      typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal;
    const res = await fetch(
      url,
      canTimeout
        ? { signal: AbortSignal.timeout(CHUNK_CHECK_TIMEOUT_MS) }
        : undefined
    );
    const contentType = res.headers.get('content-type') ?? '';
    if (/html/i.test(contentType)) {
      return { kind: 'html', url };
    }
    if (res.ok && /(java|ecma)script/i.test(contentType)) {
      return { kind: 'js', url };
    }
    return { kind: 'unknown', url };
  } catch {
    return { kind: 'unknown', url };
  }
};

// A route usually imports several chunks at once, so a single stale deploy fires
// `vite:preloadError` once per failing chunk within the same document. They all
// share one cause, and one reload either recovers all of them or none, so only
// the first is acted on.
//
// Without this, the second event of a document read the reload marker the first
// had just written, mistook it for evidence of a *completed* reload, and so both
// reported a false "Failed to fetch chunk after reload" and deleted the marker —
// leaving the reload the first event went on to perform unguarded. The next
// document then repeated the whole cycle, reloading and reporting forever
// (Sentry WEB-CLIENT-1H9 / WEB-CLIENT-VH, ~50 and ~380 events per affected
// user).
//
// Deliberately not reset on the no-op paths below: a document whose first
// failure needs no action is an unsupported browser or a blocked best-effort
// chunk, and a reload wouldn't help a second failure either.
let hasHandledPreloadError = false;

/** Test seam: each test needs a fresh document's worth of state. */
export const resetPreloadErrorHandled = () => {
  hasHandledPreloadError = false;
};

// A marker this document wrote itself is not evidence that a reload happened —
// it means we are mid-reload, before the navigation commits. `timeOrigin` is the
// current document's start in epoch milliseconds, the same clock as `Date.now`,
// so anything at or after it belongs to this document.
const isMarkerFromThisDocument = (markedAt: number): boolean =>
  typeof performance !== 'undefined' &&
  typeof performance.timeOrigin === 'number' &&
  markedAt >= performance.timeOrigin;

/**
 * Handle a `vite:preloadError`: reload once to pick up the current deployment's
 * chunks, and report to Sentry only when a reload has already failed to fix it.
 */
export const handlePreloadError = async (payload?: PreloadErrorPayload) => {
  // A blocked or failed load of a best-effort chunk (LogRocket session
  // recording, routinely blocked by ad/privacy blockers and network filters)
  // must not reload the page or report to Sentry — its caller already tolerates
  // a missing SDK, so reloading is disruptive and the report is pure noise
  // (Sentry WEB-CLIENT-1H9). Checked before claiming the one-per-document slot
  // so it never masks a genuine chunk failure in the same document.
  if (isBestEffortChunk(getChunkUrl(payload))) {
    return;
  }

  if (hasHandledPreloadError) {
    return;
  }
  hasHandledPreloadError = true;

  if (payload?.name === 'SyntaxError') {
    const { kind, url } = await classifyFailedChunk(payload);
    if (kind === 'js') {
      // Valid JS an unsupported browser can't parse — a reload can't help, so
      // don't reload or report.
      return;
    }
    if (kind === 'html') {
      // The chunk is missing and the backend returned index.html in its place.
      Sentry.captureMessage('Chunk request returned HTML instead of JS', {
        level: 'warning',
        extra: { url, online: navigator.onLine },
      });
    }
  }

  try {
    const markedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    const now = Date.now();
    const reloadAlreadyFailed =
      markedAt > 0 &&
      now - markedAt < CHUNK_RELOAD_WINDOW_MS &&
      !isMarkerFromThisDocument(markedAt);

    if (reloadAlreadyFailed) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      Sentry.captureMessage('Failed to fetch chunk after reload', {
        level: 'warning',
        extra: { error: payload, online: navigator.onLine },
      });
      return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.) — fall through to reload
  }

  window.location.reload();
};
